import React, { useState } from 'react';
import {
	Box,
	Card,
	CardContent,
	Chip,
	Checkbox,
	CircularProgress,
	FormControlLabel,
	IconButton,
	Link,
	Tooltip,
	Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ReceiptIcon from '@mui/icons-material/Receipt';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { IPassenger, IReceipt, PaymentReceivedRelationTypeEnum, PaymentReceivedPlatformTypeEnum } from '../../types';
import { issueReceipt, reconcileReceipt } from '../../services';
import { GenerateReceiptModal } from './GenerateReceiptModal';
import { getCardStampLabel } from '../ClientPaymentsList/ClientPaymentsFilters';

// ─── Labels y colores ────────────────────────────────────────────────────────

const RELATION_LABEL: Record<PaymentReceivedRelationTypeEnum, string> = {
	[PaymentReceivedRelationTypeEnum.MVOrderTransaction]: 'C2A / Tarjeta',
	[PaymentReceivedRelationTypeEnum.VNOrderTransaction]: 'VN Transacción',
	[PaymentReceivedRelationTypeEnum.CashPayment]: 'Efectivo',
	[PaymentReceivedRelationTypeEnum.WireTransferPayment]: 'Transferencia',
	[PaymentReceivedRelationTypeEnum.MVHotelReserve]: 'MV Hotel',
	[PaymentReceivedRelationTypeEnum.VNFlightReserve]: 'VN Vuelo',
	[PaymentReceivedRelationTypeEnum.VNHotelReserveMV]: 'VN Hotel MV',
};

const RELATION_COLOR: Record<
	PaymentReceivedRelationTypeEnum,
	'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'info'
> = {
	[PaymentReceivedRelationTypeEnum.MVOrderTransaction]: 'primary',
	[PaymentReceivedRelationTypeEnum.VNOrderTransaction]: 'secondary',
	[PaymentReceivedRelationTypeEnum.CashPayment]: 'warning',
	[PaymentReceivedRelationTypeEnum.WireTransferPayment]: 'info',
	[PaymentReceivedRelationTypeEnum.MVHotelReserve]: 'default',
	[PaymentReceivedRelationTypeEnum.VNFlightReserve]: 'default',
	[PaymentReceivedRelationTypeEnum.VNHotelReserveMV]: 'default',
};

const PLATFORM_LABEL: Record<PaymentReceivedPlatformTypeEnum, string> = {
	[PaymentReceivedPlatformTypeEnum.PaymentSimulator]: 'Tarjeta contra Aerolinea',
	[PaymentReceivedPlatformTypeEnum.SiemprePago]: 'SiemprePago',
	[PaymentReceivedPlatformTypeEnum.Bancard]: 'Bancard',
	[PaymentReceivedPlatformTypeEnum.ItauPoints]: 'Puntos Itaú',
	[PaymentReceivedPlatformTypeEnum.ATCRedEnlace]: 'ATC Red Enlace',
	[PaymentReceivedPlatformTypeEnum.Wompi]: 'Wompi',
	[PaymentReceivedPlatformTypeEnum.Cash]: 'Efectivo',
	[PaymentReceivedPlatformTypeEnum.WireTransfer]: 'Wire Transfer',
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatDate = (iso: string): string => {
	try {
		return new Date(iso).toLocaleString('es-UY', {
			day: '2-digit',
			month: '2-digit',
			year: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
		});
	} catch {
		return iso;
	}
};

const formatAmount = (amount: number, currency: string): string => {
	return (
		new Intl.NumberFormat('es-UY', {
			style: 'decimal',
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		}).format(amount) + ` ${currency}`
	);
};

// ─── Sub-componente: fila de detalle ext_ ────────────────────────────────────

const DetailRow = ({ label, value }: { label: string; value?: string | number }) => {
	if (!value && value !== 0) return null;
	return (
		<Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
			<Typography variant="caption" color="text.secondary">
				{label}:
			</Typography>
			<Typography variant="caption" fontWeight="medium">
				{value}
			</Typography>
		</Box>
	);
};

// ─── CopyButton inline ───────────────────────────────────────────────────────

const CopyButton = ({ value }: { value: string }) => {
	const [copied, setCopied] = useState(false);
	const handleCopy = () => {
		navigator.clipboard.writeText(value);
		setCopied(true);
		setTimeout(() => setCopied(false), 2000);
	};
	return (
		<Tooltip title={copied ? '¡Copiado!' : 'Copiar'}>
			<IconButton size="small" onClick={handleCopy} sx={{ p: 0.25 }}>
				<ContentCopyIcon sx={{ fontSize: 14, color: copied ? 'success.main' : 'text.secondary' }} />
			</IconButton>
		</Tooltip>
	);
};

// ─── Props ───────────────────────────────────────────────────────────────────

interface Props {
	payment: IReceipt;
	passengers: IPassenger[];
	/** Puede marcar como conciliado (isOperatorOrHigher) */
	canReconcile: boolean;
	/** Puede deshacer la conciliación (isSuperAdmin) */
	canUnreconcile: boolean;
	onUpdated: (updated: IReceipt) => void;
}

// ─── Card principal ───────────────────────────────────────────────────────────

export const ClientPaymentCard = ({
	payment,
	passengers,
	canReconcile,
	canUnreconcile,
	onUpdated,
}: Props): JSX.Element => {
	const [generatingPdf, setGeneratingPdf] = useState(false);
	const [reconcilingLoading, setReconcilingLoading] = useState(false);
	const [localPayment, setLocalPayment] = useState<IReceipt>(payment);
	const [receiptModalOpen, setReceiptModalOpen] = useState(false);

	const invoiceablePassengers = passengers.filter((p) => p.invoiceable);

	const isCurrencyUSD = localPayment.transactionCurrency === 'USD';
	const hasExtData =
		localPayment.extBankName ||
		localPayment.extBankAccount ||
		localPayment.extPaymentReference ||
		localPayment.extAuthCode ||
		localPayment.extCardStamp ||
		localPayment.extCardLast4 ||
		localPayment.extBranchName ||
		localPayment.extCashSourceType ||
		localPayment.extGatewayTransactionId ||
		localPayment.extAirlineName;

	const relationLabel = RELATION_LABEL[localPayment.relationType] ?? localPayment.relationType;
	const platformLabel = PLATFORM_LABEL[localPayment.platformType] ?? localPayment.platformType;
	const showPlatformChip = platformLabel !== relationLabel;

	const handleGenerateReceiptClick = () => {
		if (invoiceablePassengers.length === 1) {
			// Único pasajero facturable: avanzar directo
			const pax = invoiceablePassengers[0];
			doGenerateReceipt(pax.id || pax.keyId);
		} else {
			// 0 pasajeros (warning) o 2+ (selección): abrir modal
			setReceiptModalOpen(true);
		}
	};

	const doGenerateReceipt = async (passengerId: string) => {
		setGeneratingPdf(true);
		try {
			const res = await issueReceipt(localPayment.id, passengerId);
			const updated = { ...localPayment, issued: true, pdfUrl: res.data.pdfUrl, receiptNumber: res.data.receiptNumber };
			setLocalPayment(updated);
			onUpdated(updated);
			setReceiptModalOpen(false);
		} catch {
			alert('Error al generar el recibo. Intente de nuevo.');
		} finally {
			setGeneratingPdf(false);
		}
	};

	const handleReconcile = async (checked: boolean) => {
		setReconcilingLoading(true);
		try {
			const res = await reconcileReceipt(localPayment.id, checked);
			setLocalPayment(res.data);
			onUpdated(res.data);
		} catch {
			alert('Error al actualizar la conciliación.');
		} finally {
			setReconcilingLoading(false);
		}
	};

	return (
		<>
			<Card
				variant="outlined"
				sx={{
					borderRadius: 2,
					borderColor: localPayment.reconciled ? 'success.light' : 'divider',
					backgroundColor: localPayment.reconciled ? 'rgba(46,125,50,0.04)' : 'background.paper',
					transition: 'border-color 0.2s',
				}}
			>
				<CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
					{/*
					 * Layout horizontal en desktop:
					 * [chips+fecha] | [monto+obs+ext] | [acciones]
					 * En mobile (xs) se apila verticalmente.
					 */}
					<Box
						sx={{
							display: 'flex',
							flexDirection: { xs: 'column', sm: 'row' },
							alignItems: { xs: 'flex-start', sm: 'center' },
							gap: { xs: 1, sm: 0 },
						}}
					>
						{/* — Col 1: chips + fecha — */}
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'column',
								gap: 0.5,
								width: { xs: '100%', sm: 190 },
								flexShrink: 0,
								pr: { sm: 2 },
							}}
						>
							<Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
								<Chip
									label={relationLabel}
									color={RELATION_COLOR[localPayment.relationType] ?? 'default'}
									size="small"
								/>
								{showPlatformChip && <Chip label={platformLabel} variant="outlined" size="small" />}
							</Box>
							<Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
								{formatDate(localPayment.createdAt)}
							</Typography>
						</Box>

						{/* — Col 2: monto + detalles (flex: 1) — */}
						<Box sx={{ flex: 1, px: { sm: 2 }, borderLeft: { sm: '1px solid' }, borderColor: { sm: 'divider' } }}>
							<Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
								<Typography variant="subtitle1" fontWeight="bold">
									{formatAmount(localPayment.transactionAmount, localPayment.transactionCurrency)}
								</Typography>
								{!isCurrencyUSD && (
									<Typography variant="caption" color="text.secondary">
										≈ USD {localPayment.amountInMainCurrency.toFixed(2)}
									</Typography>
								)}
							</Box>

							{localPayment.observations && (
								<Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', display: 'block' }}>
									&ldquo;{localPayment.observations}&rdquo;
								</Typography>
							)}

							{localPayment.authorizationId && (
								<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
									<Typography variant="caption" color="text.secondary">
										Auth ID:
									</Typography>
									<Typography variant="caption" fontFamily="monospace" fontWeight="medium">
										{localPayment.authorizationId}
									</Typography>
									<CopyButton value={String(localPayment.authorizationId)} />
								</Box>
							)}

							{hasExtData && (
								<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 0.5 }}>
									<DetailRow label="Aerolinea" value={localPayment.extAirlineName} />
									<DetailRow label="Banco" value={localPayment.extBankName} />
									<DetailRow label="Cuenta" value={localPayment.extBankAccount} />
									<DetailRow label="Ref." value={localPayment.extPaymentReference} />
									<DetailRow
										label="Tarjeta"
										value={
											localPayment.extCardStamp
												? `${getCardStampLabel(localPayment.extCardStamp)}${
														localPayment.extCardLast4 ? ` ****${localPayment.extCardLast4}` : ''
												  }`
												: localPayment.extCardLast4
												? `****${localPayment.extCardLast4}`
												: undefined
										}
									/>
									<DetailRow label="Gateway" value={localPayment.extGatewayTransactionId} />
								</Box>
							)}
						</Box>

						{/* — Col 3: acciones — */}
						<Box
							sx={{
								display: 'flex',
								flexDirection: { xs: 'row', sm: 'column' },
								alignItems: { xs: 'center', sm: 'flex-end' },
								gap: 0.75,
								pl: { sm: 2 },
								borderLeft: { sm: '1px solid' },
								borderColor: { sm: 'divider' },
								width: { xs: '100%', sm: 210 },
								flexShrink: 0,
							}}
						>
							{/* Conciliación */}
							{reconcilingLoading ? (
								<CircularProgress size={16} />
							) : localPayment.reconciled ? (
								/* Ya conciliado: todos lo ven, solo superAdmin puede desconciliar */
								<FormControlLabel
									sx={{ m: 0 }}
									control={
										<Checkbox
											checked
											onChange={() => handleReconcile(false)}
											disabled={!canUnreconcile}
											size="small"
											color="success"
											checkedIcon={<CheckCircleOutlineIcon />}
											sx={{ p: 0.5 }}
										/>
									}
									label={
										<Typography variant="caption" color="success.main">
											{`Conciliado${localPayment.reconciledBy ? ` por ${localPayment.reconciledBy}` : ''}`}
										</Typography>
									}
								/>
							) : canReconcile ? (
								/* No conciliado: solo operatorOrHigher puede marcarlo */
								<FormControlLabel
									sx={{ m: 0 }}
									control={
										<Checkbox
											checked={false}
											onChange={() => handleReconcile(true)}
											size="small"
											color="success"
											icon={<RadioButtonUncheckedIcon />}
											checkedIcon={<CheckCircleOutlineIcon />}
											sx={{ p: 0.5 }}
										/>
									}
									label={
										<Typography variant="caption" color="text.secondary">
											Marcar conciliado
										</Typography>
									}
								/>
							) : null}

							{/* Recibo */}
							{localPayment.issued && localPayment.pdfUrl ? (
								<Link href={localPayment.pdfUrl} target="_blank" rel="noopener noreferrer" underline="none">
									<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'primary.main' }}>
										<ReceiptIcon sx={{ fontSize: 15 }} />
										<Typography variant="caption" fontWeight="medium">
											{localPayment.receiptNumber ?? 'Ver recibo'}
										</Typography>
										<OpenInNewIcon sx={{ fontSize: 12 }} />
									</Box>
								</Link>
							) : (
								<Box
									onClick={generatingPdf ? undefined : handleGenerateReceiptClick}
									sx={{
										display: 'flex',
										alignItems: 'center',
										gap: 0.5,
										color: generatingPdf ? 'text.disabled' : 'primary.main',
										cursor: generatingPdf ? 'default' : 'pointer',
										'&:hover': { opacity: generatingPdf ? 1 : 0.75 },
									}}
								>
									{generatingPdf ? <CircularProgress size={14} /> : <ReceiptIcon sx={{ fontSize: 15 }} />}
									<Typography variant="caption" fontWeight="medium">
										{generatingPdf ? 'Generando...' : 'Generar recibo'}
									</Typography>
								</Box>
							)}
						</Box>
					</Box>
				</CardContent>
			</Card>

			<GenerateReceiptModal
				open={receiptModalOpen}
				invoiceablePassengers={invoiceablePassengers}
				loading={generatingPdf}
				onConfirm={doGenerateReceipt}
				onClose={() => setReceiptModalOpen(false)}
			/>
		</>
	);
};
